import React from "react";
import { motion } from "framer-motion";
import moment from "moment-timezone";
import { Clock, Sun, Sunrise, Sunset, Moon, Activity as ActivityIcon, RefreshCw, Square, Zap, AlertCircle } from "lucide-react";
import { useDashTheme } from "../hooks/useDashTheme";
import { useAiStream } from "../hooks/useAiStream";
import { SectionCard, Button } from "./primitives";
import { EmptyState } from "./DataTable";
import { HourlyReportRenderer, HourlyMetricsStrip } from "./HourlyReportRenderer";
import AILimitModal from "../../../components/AILimitModal/AILimitModal";
import { SERVER_URL } from "../../../utils/serverConfig";

// "10:00-11:00" → { startH:10, endH:11, label:"10–11", ampm:"AM" }
function parseHourSlot(hourSlot) {
  const [a, b] = (hourSlot || "").split("-");
  const startH = parseInt((a || "").split(":")[0], 10);
  const endH = parseInt((b || "").split(":")[0], 10);
  const to12 = (h) => { const x = h % 12 || 12; return x; };
  const ampm = startH < 12 ? "AM" : "PM";
  return { startH, endH, label: `${to12(startH)}–${to12(endH)}`, ampm };
}

// Part-of-day icon for an hour (gives the rail a day-rhythm feel).
function partIcon(h, size = 14) {
  if (h >= 5 && h < 11) return <Sunrise size={size} />;
  if (h >= 11 && h < 16) return <Sun size={size} />;
  if (h >= 16 && h < 20) return <Sunset size={size} />;
  return <Moon size={size} />;
}
function partLabel(h) {
  if (h >= 5 && h < 11) return "Morning";
  if (h >= 11 && h < 16) return "Afternoon";
  if (h >= 16 && h < 20) return "Evening";
  return "Night";
}

/**
 * HourlyInsightsV2 — V2-themed, hourly-aware insights.
 * Same data/endpoints as the legacy HourlyInsightsView:
 *   GET  /api/v1/hourly-insights/slots?userId&date   → tracked hour slots
 *   POST /api/v1/hourly-insights/stream {userId,date,hourSlot,forceRegenerate}
 * The per-hour summary is rendered through the shared SummaryReport so it
 * matches the Daily Summary look.
 */
export function HourlyInsightsV2({ accessibleUsers, selectedUserId, selectedDate }) {
  const t = useDashTheme();
  const stream = useAiStream("/api/v1/hourly-insights/metrics-stream");

  const [slots, setSlots] = React.useState([]);
  const [loadingSlots, setLoadingSlots] = React.useState(false);
  const [slotError, setSlotError] = React.useState(null);
  const [selectedSlot, setSelectedSlot] = React.useState(null);

  const selectedUser = accessibleUsers?.find((u) => (u._id || u.id) === selectedUserId);

  // Fetch hour slots whenever user/date changes.
  React.useEffect(() => {
    if (!selectedUserId || !selectedDate) return;
    let alive = true;
    (async () => {
      setLoadingSlots(true); setSlotError(null); setSelectedSlot(null); setSlots([]); stream.reset?.();
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${SERVER_URL}/api/v1/hourly-insights/slots?userId=${selectedUserId}&date=${selectedDate}`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (!alive) return;
        if (data.success && data.data?.slots) {
          setSlots(data.data.slots);
          if (data.data.slots.length === 0) setSlotError("No activity recorded for this date");
        } else setSlotError(data.message || "Failed to fetch hourly slots");
      } catch (_) { if (alive) setSlotError("Failed to load hourly slots"); }
      finally { if (alive) setLoadingSlots(false); }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserId, selectedDate]);

  const runSlot = (slot, force = false) => {
    setSelectedSlot(slot);
    stream.run({ userId: selectedUserId, date: selectedDate, hourSlot: slot.hourSlot, forceRegenerate: force });
  };

  const hasContent = stream.metrics != null || stream.report != null;
  const sel = selectedSlot ? parseHourSlot(selectedSlot.hourSlot) : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: t.s[5] }}>
      {/* Hour rail */}
      <SectionCard title="Tracked hours" subtitle={selectedDate ? moment.tz(selectedDate, "Asia/Kolkata").format("dddd, MMMM D, YYYY") : "Pick a member and date"} icon={<Clock size={16} />}>
        {!selectedUserId ? (
          <EmptyState icon={<ActivityIcon size={22} />} title="Select a member" description="Choose a team member and date to see their tracked hours." />
        ) : loadingSlots ? (
          <div style={{ display: "flex", gap: t.s[3], flexWrap: "wrap" }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} style={{ width: 92, height: 64, borderRadius: t.r.md, background: t.c.surfaceMuted, border: `1px solid ${t.c.border}` }} />
            ))}
          </div>
        ) : slotError ? (
          <EmptyState icon={<ActivityIcon size={22} />} title="No tracked hours" description={slotError} />
        ) : (
          <div style={{ display: "flex", gap: t.s[3], flexWrap: "wrap" }}>
            {slots.map((slot, i) => {
              const p = parseHourSlot(slot.hourSlot);
              const active = selectedSlot?.hourSlot === slot.hourSlot;
              return (
                <motion.button
                  key={slot.hourSlot}
                  initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.03, 0.25) }}
                  whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}
                  onClick={() => runSlot(slot)}
                  style={{
                    position: "relative", minWidth: 92, padding: "10px 14px", borderRadius: t.r.md, cursor: "pointer", textAlign: "left",
                    border: `1px solid ${active ? "transparent" : t.c.border}`,
                    background: active ? t.c.primary : t.c.surface,
                    color: active ? "#fff" : t.c.text,
                    boxShadow: active ? `0 6px 18px ${t.c.glow}` : t.shadow.sm,
                    fontFamily: t.font.family, transition: "background 140ms, box-shadow 140ms, border-color 140ms",
                  }}
                >
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: t.font.size.xs, fontWeight: 600, opacity: active ? 0.9 : 0.7, color: active ? "#fff" : t.c.textTertiary }}>
                    {partIcon(p.startH, 12)} {partLabel(p.startH)}
                  </span>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginTop: 4 }}>
                    <span style={{ fontSize: t.font.size.lg, fontWeight: 700, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>{p.label}</span>
                    <span style={{ fontSize: t.font.size.xs, fontWeight: 600, opacity: 0.8 }}>{p.ampm}</span>
                  </div>
                </motion.button>
              );
            })}
          </div>
        )}
      </SectionCard>

      {/* Selected hour summary */}
      {selectedSlot && (
        <SectionCard
          title={
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 26, height: 26, borderRadius: 8, background: t.c.primarySoft, color: t.c.primary }}>{partIcon(sel.startH, 14)}</span>
              {sel.label} {sel.ampm} · {partLabel(sel.startH)}
            </span>
          }
          subtitle={`${selectedUser ? `${selectedUser.firstName || ""} ${selectedUser.lastName || ""}`.trim() : ""}${selectedDate ? ` · ${moment.tz(selectedDate, "Asia/Kolkata").format("MMM D, YYYY")}` : ""}`}
          icon={<Clock size={16} />}
          actions={
            <div style={{ display: "inline-flex", alignItems: "center", gap: t.s[2] }}>
              {stream.cached && <span style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, display: "inline-flex", alignItems: "center", gap: 4 }}><Zap size={12} /> cached</span>}
              {stream.streaming ? (
                <Button variant="secondary" size="sm" leftIcon={<Square size={13} />} onClick={stream.stop}>Stop</Button>
              ) : hasContent ? (
                <Button variant="secondary" size="sm" leftIcon={<RefreshCw size={13} />} onClick={() => runSlot(selectedSlot, true)}>Regenerate</Button>
              ) : null}
            </div>
          }
        >
          {stream.error ? (
            <EmptyState icon={<AlertCircle size={22} />} title="No activity found" description={/no tracked|no activity/i.test(stream.error) ? stream.error.replace(/\*\*/g, "") : stream.error} />
          ) : !hasContent && stream.streaming ? (
            <HourLoader t={t} />
          ) : !hasContent ? (
            <EmptyState icon={<Clock size={22} />} title="Ready to analyze this hour" description="Generating the AI breakdown for this time slot…" />
          ) : (
            <div>
              {stream.streaming && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: t.s[4], fontSize: t.font.size.sm, color: t.c.primary }}>
                  <motion.span animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} style={{ display: "inline-flex" }}><Clock size={14} /></motion.span>
                  Analyzing this hour…
                </div>
              )}
              {/* ① Instant metrics strip (from `metrics` event) */}
              <HourlyMetricsStrip metrics={stream.metrics} streaming={stream.streaming && !stream.report} />
              {/* ② Progressive structured report (micro-segments stream in) */}
              {stream.report && (
                <HourlyReportRenderer report={stream.report} streaming={stream.streaming} partial={stream.reportPartial} />
              )}
            </div>
          )}
        </SectionCard>
      )}

      <AILimitModal isOpen={stream.limitReached} onClose={() => stream.setLimitReached(false)} />
    </div>
  );
}

function HourLoader({ t }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: `${t.s[8]}px ${t.s[5]}px`, gap: t.s[3] }}>
      <motion.span animate={{ scale: [1, 1.12, 1], opacity: [0.7, 1, 0.7] }} transition={{ duration: 1.4, repeat: Infinity }}
        style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 52, height: 52, borderRadius: 16, background: t.c.primaryGradient, color: "#fff", boxShadow: `0 8px 24px ${t.c.glow}` }}>
        <Clock size={24} />
      </motion.span>
      <div style={{ fontSize: t.font.size.lg, fontWeight: 600, color: t.c.text }}>Analyzing this hour…</div>
      <div style={{ fontSize: t.font.size.sm, color: t.c.textTertiary }}>Breaking down activity for the selected slot</div>
    </div>
  );
}

export default HourlyInsightsV2;
